/**
 * Baús do mundo (por região). Únicos: salvam abertos (via Inventory.openedChests).
 * Player aproxima -> USAR -> loot rápido. Só os baús da região ativa ficam visíveis (setRegion).
 *
 * 10.9CD — REDESIGN + ANIMAÇÃO DE ABERTURA.
 * Três famílias visuais (mesmo tamanho ~1.3 m e mesma hitbox de antes):
 *   comum  (wooden/ruined/skyruin): madeira, tampa ABAULADA (meio-cilindro), 3 cintas, 4 cantoneiras,
 *                                   fechadura frontal. ruined = escuro + musgo; skyruin = pedra clara + runas.
 *   raro   (void/shard):            pedra escura, cristais nas laterais, runa hexagonal emissiva na tampa,
 *                                   5 fagulhas orbitando devagar.
 *   boss   (alpha/colossus/tempest): 1.5x maior, metal escuro, chifres na tampa, gema grande, anel de
 *                                   energia pulsando no chão.
 * Idle: brilho pulsando na FRESTA (plano fino entre base e tampa) + gema pulsando + feixe sutil.
 * Abertura (~0.9 s): tremor/squash → tampa abre com easeOutBack → explosão (disco no chão que cresce,
 * fagulhas em arco com gravidade, feixe vertical que sobe e apaga) → interior emissivo fraco, gema apagada.
 * refresh() com baú já aberto pula direto pro estado final (sem explosão).
 * Custo: nenhuma PointLight; castShadow só na base; geometrias compartilhadas; materiais animados = clone.
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { crystalStdMat, energyMat, metalMat, stoneMat } from "./Materials";

export type ChestType = "wooden" | "void" | "alpha" | "ruined" | "shard" | "colossus" | "skyruin" | "tempest";

type Family = "common" | "rare" | "boss";

interface Palette {
  body: number; // madeira / pedra / metal do corpo
  metal: number; // ferragens
  glow: number; // cor da energia (gema, fresta, explosão)
}

const PALETTE: Record<ChestType, Palette> = {
  wooden: { body: 0x8a5a2b, metal: 0x6e6a60, glow: 0xffd27a },
  ruined: { body: 0x4a3a2a, metal: 0x4c4a44, glow: 0x9fe08a },
  skyruin: { body: 0xb9c4d6, metal: 0x8894a8, glow: 0xdfeeff },
  void: { body: 0x2a2140, metal: 0x4b3a7a, glow: 0x7c4dff },
  shard: { body: 0x1f3446, metal: 0x3a6a80, glow: 0x62e8ff },
  alpha: { body: 0x2b2430, metal: 0x5a4650, glow: 0xff5a6a },
  colossus: { body: 0x232a3a, metal: 0xffd54a, glow: 0x62e8ff },
  tempest: { body: 0x1f2a3c, metal: 0x4a6a90, glow: 0x6fb6ff },
};

const FAMILY: Record<ChestType, Family> = {
  wooden: "common",
  ruined: "common",
  skyruin: "common",
  void: "rare",
  shard: "rare",
  alpha: "boss",
  colossus: "boss",
  tempest: "boss",
};

// ---- geometrias compartilhadas (baús vivem o jogo inteiro; nada é descartado) ----
const G = {
  base: new THREE.BoxGeometry(1.25, 0.7, 0.85),
  band: new THREE.BoxGeometry(0.12, 0.74, 0.9),
  corner: new THREE.BoxGeometry(0.16, 0.2, 0.16),
  lock: new THREE.BoxGeometry(0.22, 0.26, 0.08),
  keyhole: new THREE.BoxGeometry(0.05, 0.1, 0.02),
  // tampa abaulada: meio-cilindro deitado no eixo X (thetaLength = PI → só a metade de cima)
  dome: new THREE.CylinderGeometry(0.44, 0.44, 1.3, 10, 1, false, 0, Math.PI),
  domeCap: new THREE.BoxGeometry(1.3, 0.12, 0.9),
  domeBand: new THREE.CylinderGeometry(0.46, 0.46, 0.14, 10, 1, false, 0, Math.PI),
  lidFlat: new THREE.BoxGeometry(1.3, 0.28, 0.9),
  lidBand: new THREE.BoxGeometry(1.34, 0.16, 0.14),
  rune: new THREE.CylinderGeometry(0.22, 0.22, 0.04, 6),
  runeSmall: new THREE.BoxGeometry(0.1, 0.02, 0.18),
  moss: new THREE.SphereGeometry(0.16, 6, 4),
  crystal: new THREE.OctahedronGeometry(0.16, 0),
  gem: new THREE.OctahedronGeometry(0.17, 0),
  gemBig: new THREE.OctahedronGeometry(0.24, 0),
  horn: new THREE.ConeGeometry(0.09, 0.42, 5),
  ring: new THREE.RingGeometry(0.85, 1.05, 24),
  disc: new THREE.CircleGeometry(1, 24),
  slit: new THREE.PlaneGeometry(1.26, 0.06),
  interior: new THREE.BoxGeometry(1.1, 0.5, 0.7),
  beamIdle: new THREE.CylinderGeometry(0.12, 0.12, 2.4, 6, 1, true),
  beamBurst: new THREE.CylinderGeometry(0.28, 0.14, 1, 8, 1, true), // altura 1 → escalado em y
  spark: new THREE.OctahedronGeometry(0.06, 0),
  orb: new THREE.OctahedronGeometry(0.05, 0),
};

const SPARK_COUNT = 14;
const OPEN_DURATION = 0.9; // segundos

interface Spark {
  mesh: THREE.Mesh;
  vx: number;
  vy: number;
  vz: number;
  spin: number;
}

interface WorldChest {
  id: string;
  type: ChestType;
  family: Family;
  region: string;
  group: THREE.Group;
  body: THREE.Group; // base+tampa: recebe o tremor/squash
  lid: THREE.Group;
  // materiais animados (clones)
  gemMat: THREE.MeshStandardMaterial;
  slitMat: THREE.MeshBasicMaterial;
  beamIdleMat: THREE.MeshBasicMaterial;
  interiorMat: THREE.MeshStandardMaterial;
  ringMat: THREE.MeshBasicMaterial | null; // boss
  // explosão
  disc: THREE.Mesh;
  discMat: THREE.MeshBasicMaterial;
  beamBurst: THREE.Mesh;
  beamBurstMat: THREE.MeshBasicMaterial;
  sparks: Spark[];
  fx: THREE.Group; // contém disco + fagulhas + feixe (fora do body → não treme)
  // extras animados
  orbs: THREE.Mesh[]; // raros: fagulhas orbitando
  beamIdle: THREE.Mesh;
  gem: THREE.Mesh;
  opened: boolean;
  openAnim: number; // 0..1 progresso da abertura
  animating: boolean; // true só durante a abertura tocada em jogo
  pos: THREE.Vector3;
  phase: number; // desloca os pulsos pra baús vizinhos não piscarem juntos
}

// ---- easings ----
const easeOutBack = (t: number): number => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};
const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
/** t normalizado dentro do intervalo [a,b] de openAnim. */
const seg = (t: number, a: number, b: number): number => clamp01((t - a) / (b - a));

export class ChestManager {
  private readonly chests: WorldChest[] = [];
  private readonly cfg = GameConfig.chests;
  private activeRegion: string = GameConfig.world.startRegion;
  private bob = 0;

  constructor(scene: THREE.Scene) {
    let i = 0;
    for (const c of this.cfg.world) {
      const chest = this.build(c.type as ChestType, (c as { region?: string }).region ?? "forgottenForest", i++);
      chest.id = c.id;
      chest.group.position.set(c.x, 0, c.z);
      chest.pos = chest.group.position;
      scene.add(chest.group);
      this.chests.push(chest);
    }
    this.setRegion(this.activeRegion);
  }

  /** Monta um baú da família do tipo. */
  private build(type: ChestType, region: string, index: number): WorldChest {
    const pal = PALETTE[type];
    const family = FAMILY[type];
    const group = new THREE.Group();
    const body = new THREE.Group();
    group.add(body);
    const scale = family === "boss" ? 1.5 : 1;
    body.scale.setScalar(scale);

    const bodyMat = family === "common" && type !== "skyruin"
      ? new THREE.MeshStandardMaterial({ color: pal.body, roughness: 0.85, metalness: 0.05, flatShading: true })
      : family === "boss"
        ? metalMat(pal.body)
        : stoneMat(pal.body);
    const ironMat = metalMat(pal.metal);

    // ---- BASE (sombra só aqui) ----
    const base = new THREE.Mesh(G.base, bodyMat);
    base.position.y = 0.35;
    base.castShadow = true;
    body.add(base);

    // interior emissivo fraco (aparece com a tampa aberta)
    const interiorMat = new THREE.MeshStandardMaterial({ color: 0x0a0812, emissive: pal.glow, emissiveIntensity: 0, roughness: 1 });
    const interior = new THREE.Mesh(G.interior, interiorMat);
    interior.position.y = 0.46;
    body.add(interior);

    // ---- TAMPA (dobradiça na aresta traseira, z negativo) ----
    const lid = new THREE.Group();
    lid.position.set(0, 0.7, -0.42);
    body.add(lid);

    // gema (material clonado: pulsa/apaga por baú)
    const gemMat = crystalStdMat(pal.glow).clone();
    gemMat.emissiveIntensity = 1.1;
    let gem: THREE.Mesh;

    if (family === "common") {
      // tampa abaulada: meio-cilindro deitado + tampa reta por baixo pra fechar
      const dome = new THREE.Mesh(G.dome, bodyMat);
      dome.rotation.z = Math.PI / 2;
      dome.rotation.y = Math.PI / 2; // meia-volta virada pra cima
      dome.position.set(0, 0.06, 0.42);
      lid.add(dome);
      const cap = new THREE.Mesh(G.domeCap, bodyMat);
      cap.position.set(0, 0.06, 0.42);
      lid.add(cap);
      // 3 cintas: 2 na base + tampa, 1 central
      for (const bx of [-0.45, 0, 0.45]) {
        const band = new THREE.Mesh(G.band, ironMat);
        band.position.set(bx, 0.36, 0);
        body.add(band);
        const lb = new THREE.Mesh(G.domeBand, ironMat);
        lb.rotation.z = Math.PI / 2;
        lb.rotation.y = Math.PI / 2;
        lb.position.set(bx, 0.06, 0.42);
        lid.add(lb);
      }
      // cantoneiras nos 4 cantos da base
      for (const cx of [-0.58, 0.58]) {
        for (const cz of [-0.38, 0.38]) {
          const corner = new THREE.Mesh(G.corner, ironMat);
          corner.position.set(cx, 0.12, cz);
          body.add(corner);
        }
      }
      // fechadura frontal
      const lock = new THREE.Mesh(G.lock, ironMat);
      lock.position.set(0, 0.6, 0.45);
      body.add(lock);
      const keyhole = new THREE.Mesh(G.keyhole, stoneMat(0x0c0a10));
      keyhole.position.set(0, 0.6, 0.495);
      body.add(keyhole);
      // gema pequena na frente da tampa
      gem = new THREE.Mesh(G.crystal, gemMat);
      gem.scale.setScalar(0.7);
      gem.position.set(0, 0.34, 0.86);
      lid.add(gem);

      if (type === "ruined") {
        // musgo: esferas achatadas verdes
        const mossMat = stoneMat(0x3f7a3a);
        const spots: [number, number, number][] = [[-0.45, 0.72, 0.2], [0.35, 0.74, -0.15], [0.55, 0.3, 0.44]];
        for (const [mx, my, mz] of spots) {
          const m = new THREE.Mesh(G.moss, mossMat);
          m.position.set(mx, my, mz);
          m.scale.set(1.2, 0.45, 1);
          body.add(m);
        }
      } else if (type === "skyruin") {
        // runas gravadas nas laterais
        const runeMat = crystalStdMat(pal.glow, pal.glow, 0.6);
        for (let k = 0; k < 4; k++) {
          const r = new THREE.Mesh(G.runeSmall, runeMat);
          r.rotation.x = Math.PI / 2;
          r.position.set(-0.3 + k * 0.2, 0.42 + (k % 2) * 0.12, 0.44);
          body.add(r);
        }
      }
    } else if (family === "rare") {
      // tampa reta de pedra + cinta + runa hexagonal emissiva
      const lidBox = new THREE.Mesh(G.lidFlat, bodyMat);
      lidBox.position.set(0, 0.14, 0.42);
      lid.add(lidBox);
      const lidBand = new THREE.Mesh(G.lidBand, ironMat);
      lidBand.position.set(0, 0.22, 0.42);
      lid.add(lidBand);
      const rune = new THREE.Mesh(G.rune, crystalStdMat(pal.glow, pal.glow, 0.7));
      rune.position.set(0, 0.29, 0.42);
      lid.add(rune);
      gem = new THREE.Mesh(G.gem, gemMat);
      gem.position.set(0, 0.42, 0.42);
      lid.add(gem);
      for (const bx of [-0.42, 0.42]) {
        const band = new THREE.Mesh(G.band, ironMat);
        band.position.set(bx, 0.36, 0);
        body.add(band);
      }
      // cristais nas laterais (inclinados pra fora)
      const cMat = crystalStdMat(pal.glow);
      const cr: [number, number, number, number][] = [[-0.68, 0.3, 0.15, 0.5], [-0.68, 0.5, -0.2, 0.35], [0.68, 0.3, -0.1, -0.5], [0.68, 0.55, 0.22, -0.35]];
      for (const [cx, cy, cz, tilt] of cr) {
        const c = new THREE.Mesh(G.crystal, cMat);
        c.position.set(cx, cy, cz);
        c.rotation.z = tilt;
        c.scale.set(0.6, 1.4, 0.6);
        body.add(c);
      }
    } else {
      // BOSS: metal escuro, tampa reta grossa, chifres, gema grande
      const lidBox = new THREE.Mesh(G.lidFlat, bodyMat);
      lidBox.position.set(0, 0.14, 0.42);
      lid.add(lidBox);
      const lidBand = new THREE.Mesh(G.lidBand, ironMat);
      lidBand.position.set(0, 0.22, 0.42);
      lid.add(lidBand);
      const hornMat = metalMat(pal.metal);
      for (const hx of [-0.5, 0.5]) {
        const horn = new THREE.Mesh(G.horn, hornMat);
        horn.position.set(hx, 0.4, 0.42);
        horn.rotation.z = hx > 0 ? -0.45 : 0.45;
        lid.add(horn);
      }
      gem = new THREE.Mesh(G.gemBig, gemMat);
      gem.position.set(0, 0.46, 0.42);
      lid.add(gem);
      for (const bx of [-0.42, 0.42]) {
        const band = new THREE.Mesh(G.band, ironMat);
        band.position.set(bx, 0.36, 0);
        body.add(band);
      }
      for (const cx of [-0.58, 0.58]) {
        for (const cz of [-0.38, 0.38]) {
          const corner = new THREE.Mesh(G.corner, ironMat);
          corner.position.set(cx, 0.12, cz);
          body.add(corner);
        }
      }
    }

    // ---- FRESTA emissiva entre base e tampa (frente) ----
    const slitMat = energyMat(pal.glow, "glow").clone();
    const slit = new THREE.Mesh(G.slit, slitMat);
    slit.position.set(0, 0.7, 0.44);
    body.add(slit);

    // ---- feixe idle sutil ----
    const beamIdleMat = energyMat(pal.glow, "glow").clone();
    beamIdleMat.opacity = 0.25;
    const beamIdle = new THREE.Mesh(G.beamIdle, beamIdleMat);
    beamIdle.position.y = 1.3 * scale;
    beamIdle.scale.setScalar(scale);
    group.add(beamIdle);

    // ---- boss: anel de energia no chão ----
    let ringMat: THREE.MeshBasicMaterial | null = null;
    if (family === "boss") {
      ringMat = energyMat(pal.glow, "ring").clone();
      const ring = new THREE.Mesh(G.ring, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.03;
      ring.scale.setScalar(scale);
      group.add(ring);
    }

    // ---- raros: orbes orbitando ----
    const orbs: THREE.Mesh[] = [];
    if (family === "rare") {
      const oMat = crystalStdMat(pal.glow, pal.glow, 1.2);
      for (let k = 0; k < 5; k++) {
        const o = new THREE.Mesh(G.orb, oMat);
        group.add(o);
        orbs.push(o);
      }
    }

    // ---- FX de explosão (invisíveis até abrir) ----
    const fx = new THREE.Group();
    fx.visible = false;
    group.add(fx);
    const discMat = energyMat(pal.glow, "disc").clone();
    const disc = new THREE.Mesh(G.disc, discMat);
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = 0.04;
    fx.add(disc);
    const beamBurstMat = energyMat(pal.glow, "glow").clone();
    const beamBurst = new THREE.Mesh(G.beamBurst, beamBurstMat);
    fx.add(beamBurst);
    const sparkMat = crystalStdMat(pal.glow, pal.glow, 1.4);
    const sparks: Spark[] = [];
    for (let k = 0; k < SPARK_COUNT; k++) {
      const m = new THREE.Mesh(G.spark, sparkMat);
      fx.add(m);
      sparks.push({ mesh: m, vx: 0, vy: 0, vz: 0, spin: 0 });
    }

    return {
      id: "", type, family, region, group, body, lid, gemMat, slitMat, beamIdleMat, interiorMat, ringMat,
      disc, discMat, beamBurst, beamBurstMat, sparks, fx, orbs, beamIdle, gem,
      opened: false, openAnim: 0, animating: false, pos: group.position, phase: index * 1.7,
    };
  }

  /** Mostra apenas os baús da região informada. */
  setRegion(regionId: string): void {
    this.activeRegion = regionId;
    for (const c of this.chests) c.group.visible = c.region === regionId;
  }

  /** Aplica o estado salvo (baús já abertos pulam direto pro estado final, sem explosão). */
  refresh(isOpen: (id: string) => boolean): void {
    for (const c of this.chests) {
      const opened = isOpen(c.id);
      c.opened = opened;
      c.openAnim = opened ? 1 : 0;
      c.animating = false;
      c.fx.visible = false;
      this.applyStatic(c);
    }
  }

  /** Visual estático do estado (fechado ou totalmente aberto). */
  private applyStatic(c: WorldChest): void {
    const open = c.openAnim >= 1;
    c.lid.rotation.x = open ? -1.15 : 0;
    c.body.position.set(0, 0, 0);
    c.body.scale.setScalar(c.family === "boss" ? 1.5 : 1);
    c.beamIdle.visible = !open;
    c.gem.visible = !open;
    c.gemMat.emissiveIntensity = open ? 0 : 1.1;
    c.slitMat.opacity = open ? 0 : 0.3;
    c.interiorMat.emissiveIntensity = open ? 0.35 : 0;
    for (const o of c.orbs) o.visible = !open;
    if (c.ringMat) c.ringMat.opacity = open ? 0.12 : 0.45;
  }

  /** Baú fechado dentro do raio de interação (o mais próximo) na região ativa. */
  nearest(playerPos: THREE.Vector3): { id: string; type: ChestType; pos: THREE.Vector3 } | null {
    let best: WorldChest | null = null;
    let bestD = this.cfg.interactRadius * this.cfg.interactRadius;
    for (const c of this.chests) {
      if (c.opened || c.region !== this.activeRegion) continue;
      const dx = c.pos.x - playerPos.x;
      const dz = c.pos.z - playerPos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 <= bestD) {
        bestD = d2;
        best = c;
      }
    }
    return best ? { id: best.id, type: best.type, pos: best.pos } : null; // 10.9EX — pos pro botao "Abrir bau"
  }

  open(id: string): void {
    const c = this.chests.find((x) => x.id === id);
    if (!c || c.opened) return;
    c.opened = true;
    c.animating = true;
    c.openAnim = 0;
    this.launchSparks(c);
  }

  /** Dá velocidade inicial às fagulhas (arco pra cima, espalhando). */
  private launchSparks(c: WorldChest): void {
    const s = c.family === "boss" ? 1.5 : 1;
    for (let k = 0; k < c.sparks.length; k++) {
      const sp = c.sparks[k];
      const a = (k / c.sparks.length) * Math.PI * 2 + Math.random() * 0.5;
      const h = (0.9 + Math.random() * 1.3) * s;
      sp.vx = Math.cos(a) * h;
      sp.vz = Math.sin(a) * h;
      sp.vy = (3.2 + Math.random() * 2.2) * s;
      sp.spin = 4 + Math.random() * 6;
      sp.mesh.position.set(0, 0.7 * s, 0);
      sp.mesh.visible = true;
    }
  }

  update(dt: number): void {
    this.bob += dt;
    for (const c of this.chests) {
      if (!c.group.visible) continue;
      if (c.animating) {
        c.openAnim = Math.min(1, c.openAnim + dt / OPEN_DURATION);
        this.animateOpen(c, dt);
        if (c.openAnim >= 1) {
          c.animating = false;
          c.fx.visible = false;
          this.applyStatic(c);
        }
      } else if (!c.opened) {
        this.idle(c);
      }
    }
  }

  /** Idle fechado: fresta e gema pulsam, feixe sutil, anel/orbes animados. */
  private idle(c: WorldChest): void {
    const t = this.bob + c.phase;
    const pulse = 0.5 + 0.5 * Math.sin(t * 2.2);
    c.slitMat.opacity = 0.18 + pulse * 0.32;
    c.gemMat.emissiveIntensity = 0.9 + pulse * 0.5;
    c.beamIdleMat.opacity = 0.2 + pulse * 0.1;
    c.gem.rotation.y = t * 0.8;
    if (c.ringMat) c.ringMat.opacity = 0.3 + pulse * 0.25;
    for (let k = 0; k < c.orbs.length; k++) {
      const a = t * 0.6 + (k / c.orbs.length) * Math.PI * 2;
      const o = c.orbs[k];
      o.position.set(Math.cos(a) * 0.95, 0.55 + Math.sin(t * 1.5 + k) * 0.15, Math.sin(a) * 0.75);
      o.rotation.set(t, t * 1.3, 0);
    }
  }

  /**
   * Abertura (openAnim 0→1 em ~0.9 s):
   *  0–0.15  tremor (jitter x/z) + squash-and-stretch em y
   *  0.15–0.6 tampa abre com easeOutBack (passa de -1.15, volta)
   *  0.4–0.9  explosão: disco cresce 0.5→3.5 m e some; fagulhas em arco c/ gravidade; feixe sobe até 6 m
   */
  private animateOpen(c: WorldChest, dt: number): void {
    const t = c.openAnim;
    const s = c.family === "boss" ? 1.5 : 1;

    // (a) tremor + squash
    const shake = 1 - seg(t, 0, 0.2);
    if (shake > 0) {
      const j = 0.035 * shake;
      c.body.position.set((Math.random() - 0.5) * j * 2, 0, (Math.random() - 0.5) * j * 2);
      const sq = 1 - 0.12 * Math.sin(seg(t, 0, 0.15) * Math.PI); // achata e volta
      c.body.scale.set(s * (2 - sq), s * sq, s * (2 - sq));
      c.slitMat.opacity = 0.3 + shake * 0.6; // fresta incandesce antes de abrir
      c.gemMat.emissiveIntensity = 1.1 + shake * 1.5;
    } else {
      c.body.position.set(0, 0, 0);
      c.body.scale.setScalar(s);
    }

    // (b) tampa com easeOutBack (overshoot ~ -1.35 → -1.15)
    const lidT = seg(t, 0.15, 0.6);
    c.lid.rotation.x = -1.15 * easeOutBack(lidT);
    if (lidT > 0) {
      c.slitMat.opacity = 0.9 * (1 - lidT);
      c.interiorMat.emissiveIntensity = 0.35 + 0.9 * (1 - lidT) * lidT * 4; // clarão do interior
      c.gemMat.emissiveIntensity = Math.max(0, 2.6 * (1 - lidT));
      c.beamIdleMat.opacity = 0.3 * (1 - lidT);
    }

    // (c) explosão
    const bT = seg(t, 0.4, 0.9);
    if (bT > 0) {
      c.fx.visible = true;
      c.beamIdle.visible = false;
      const grow = easeOutCubic(bT);
      const radius = (0.5 + 3 * grow) * s;
      c.disc.scale.setScalar(radius);
      c.discMat.opacity = 0.5 * (1 - bT);
      const beamH = 6 * s * easeOutCubic(seg(bT, 0, 0.6));
      c.beamBurst.scale.set(s, beamH, s);
      c.beamBurst.position.y = 0.7 * s + beamH * 0.5;
      c.beamBurstMat.opacity = 0.45 * (1 - seg(bT, 0.35, 1));
      for (const sp of c.sparks) {
        sp.vy -= 9 * dt;
        sp.mesh.position.x += sp.vx * dt;
        sp.mesh.position.y += sp.vy * dt;
        sp.mesh.position.z += sp.vz * dt;
        sp.mesh.rotation.x += sp.spin * dt;
        sp.mesh.rotation.y += sp.spin * 0.7 * dt;
        const fade = 1 - seg(bT, 0.5, 1);
        sp.mesh.scale.setScalar(Math.max(0.01, fade * s));
        if (sp.mesh.position.y < 0.05) sp.mesh.visible = false;
      }
      for (const o of c.orbs) o.visible = false;
      c.gem.visible = false;
      if (c.ringMat) c.ringMat.opacity = 0.45 + 0.4 * (1 - bT);
    }
  }
}
